import { Link, useLocation } from "react-router-dom";
import { HelpCircle, Moon, Sun } from "lucide-react";
import { DefinitionTooltip } from "@/components/ui/tooltip";
import { HOVER_AFFORDANCE_ONLY, TOUCH_INLINE_FALLBACK } from "@/lib/touchReveal";
import { NotificationBell } from "./NotificationBell";
import { ProfileMenu } from "./ProfileMenu";
import { HeaderAccountMenu } from "./AccountMenu";
import { useRedesignNav } from "@/lib/redesignNav";
import { PageGuidanceToggle } from "@/components/guidance/PageGuidanceToggle";
import {
  otherTheme,
  NAV_INSIGHTS_COPY,
  NAV_INSIGHTS_SUBTITLE_COPY,
  NAV_UPCOMING_COPY,
  NAV_UPCOMING_SUBTITLE_COPY,
  SETTINGS_LABEL_COPY,
  THEME_TOGGLE_TO_DARK_COPY,
  THEME_TOGGLE_TO_LIGHT_COPY,
} from "@fomofi/shared";
import { useAccountTheme } from "@/hooks/use-account-theme";

// Help entry point (feedback #6) — opens the static /help FAQ page. The
// feedback widget stays the separate bottom-right bubble (internal QA, not
// end-user support).
function HelpButton({ current }: { current: boolean }) {
  return (
    <Link
      to="/help"
      aria-label="Help and support"
      // On /help this link points at the page it is already on, so it does
      // nothing when pressed. `aria-current` is what says so; the sweep still
      // reads it as an inert control, which is correct and is recorded as such
      // rather than papered over (BEN-141 QF11).
      aria-current={current ? "page" : undefined}
      className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground transition hover:text-foreground"
    >
      <HelpCircle className="h-4 w-4" />
    </Link>
  );
}

/**
 * AN-52 (Anthony, twice): the theme switch on the dashboard. One tap swaps Light and
 * Dark on the account's one setting, the same one Profile and the setup step write.
 */
function ThemeToggleButton() {
  const { theme, setAccountTheme } = useAccountTheme();
  const toDark = theme === "light";
  return (
    <button
      type="button"
      onClick={() => setAccountTheme(otherTheme(theme))}
      aria-label={toDark ? THEME_TOGGLE_TO_DARK_COPY : THEME_TOGGLE_TO_LIGHT_COPY}
      className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground transition hover:text-foreground"
    >
      {toDark ? (
        <Moon className="h-4 w-4" aria-hidden="true" />
      ) : (
        <Sun className="h-4 w-4" aria-hidden="true" />
      )}
    </button>
  );
}

// No entries for /dashboard, /debt, /payment or /financial-analysis: those
// routes set `hideTitle` below because each owns its own in-page header, so a
// title/tooltip here would render for nobody. They previously carried strings
// that no user could reach on any device (BEN-141 S16's class, inside S10).
const PAGE_META: Record<string, { title: string; tooltip: string }> = {
  "/transactions": { title: "Transactions", tooltip: "View and categorize all your transactions" },
  "/calendar": { title: "Calendar", tooltip: "See your bills and income on a calendar view" },
  "/goals": { title: "Goals", tooltip: "Track progress toward your financial goals" },
  "/forecast": { title: "Forecast", tooltip: "AI-powered cash flow predictions" },
  "/insights": { title: "Insights", tooltip: "Spending patterns and financial insights" },
  "/budgets": { title: "Budgets", tooltip: "Zero-based budgeting: every dollar gets a job" },
  "/rules": {
    title: "Categorization Rules",
    tooltip: "Auto-categorize transactions by merchant or pattern",
  },
  // Plain names (redesign): "Upcoming", not "Recurring Transactions". This page is
  // Upcoming's list of repeating bills and income.
  "/recurring": {
    title: "Recurring Transactions",
    tooltip: "Manage recurring bills and subscriptions",
  },
  "/paycheck": {
    title: "Paycheck",
    tooltip: "Your next paycheck and per-period income vs. spending",
  },
  // Was absent, so the page fell through to the "Fomo-Fi" default and the only
  // nav item in the sidebar with no header title was the one the flow audit
  // called the biggest page-set gap. The title matches the nav label, as every
  // other entry here does.
  "/financial-analysis": {
    title: "Financial Analysis",
    tooltip: "Your detected income, bills, debt and spending, and where to correct them",
  },
  "/settings": { title: SETTINGS_LABEL_COPY, tooltip: "Account preferences and app configuration" },
  // Not "tours" (X4): Help opens the welcome guide, which is navigation, not a tour.
  "/help": { title: "Help", tooltip: "FAQs, the welcome guide, and support" },
  "/banks": { title: "Connected Banks", tooltip: "Manage your linked bank accounts via Plaid" },
  // Stripe's success_url. Without an entry the shell fell back to the generic
  // "Fomo-Fi" title on the one screen confirming the user just paid.
  "/payment/success": {
    title: "Subscription confirmed",
    tooltip: "Your Fomo-Fi free trial has started",
  },
};

/** The redesign's plain names (RD-13), shown only with the switch on: "Upcoming", not
 *  "Recurring Transactions"; this page is Upcoming's list of repeating bills and income. */
interface PageMeta {
  title: string;
  tooltip: string;
  /** Package §7: a sub-line always shown under the title (no "i"), as the boards draw it. */
  subtitle?: string;
}

/** Package §7 (NavDemo): Upcoming is one page with three views, Month · Pay periods ·
 *  List, each its own address; every view carries the page's title and sub-line. */
const UPCOMING_META: PageMeta = {
  title: NAV_UPCOMING_COPY,
  tooltip: "",
  subtitle: NAV_UPCOMING_SUBTITLE_COPY,
};

const REDESIGN_PAGE_META: Record<string, PageMeta> = {
  // RD-07: Rules lives in Settings & profile with the switch on.
  "/settings/rules": {
    title: "Categorization Rules",
    tooltip: "Auto-categorize transactions by merchant or pattern",
  },
  // Package §7 replaces RD-13's "Bills & income" title here: the List view is Upcoming's.
  "/calendar": UPCOMING_META,
  "/paycheck": UPCOMING_META,
  "/recurring": UPCOMING_META,
  "/insights": { title: NAV_INSIGHTS_COPY, tooltip: "", subtitle: NAV_INSIGHTS_SUBTITLE_COPY },
};

function getPageMeta(pathname: string, redesign: boolean): PageMeta {
  return (
    (redesign && REDESIGN_PAGE_META[pathname]) ||
    PAGE_META[pathname] || { title: "Fomo-Fi", tooltip: "" }
  );
}

export function ContentHeader() {
  const { pathname } = useLocation();
  const redesign = useRedesignNav();
  const { title, tooltip, subtitle } = getPageMeta(pathname, redesign);

  // Dashboard uses its own greeting as the page title — suppress the
  // generic title block here so the greeting sits at the top of the viewport.
  // Payment owns its own content-area header + Secure Checkout chip that
  // PPTX shows inside the page body, so suppress the shell title there too.
  // Debt Eliminator owns its own title + subtitle + "How it works" action row.
  // Financial Analysis owns its own title + analyzed-range subtitle + the
  // "Analysis Complete" / Refresh Data cluster.
  // Goals now owns its Forecast-style title and lede inside the page canvas.
  const hideTitle =
    pathname === "/home" ||
    // Plan's own header carries "Your plan" and the period switch (PL-01).
    pathname === "/plan" ||
    pathname === "/dashboard" ||
    pathname === "/goals" ||
    pathname === "/payment" ||
    pathname === "/debt" ||
    pathname === "/financial-analysis";

  // #1110: at 390 the row has 358px and the actions want 291px of it, so the
  // title column got whatever was left and the two engines disagreed about
  // what that was — Chromium clipped the title, WebKit clipped the title AND
  // squeezed the square help button down to 25px. `shrink-0` on the cluster
  // would only clip the title harder, so the header stops being one row below
  // `sm`: the title takes the full width and the actions sit under it. From
  // `sm` up nothing changes.
  //
  // Report 166 (2026-10-27): `sm` is a media query, and a media query's rem stays 16px
  // whatever the text size, so at 640 with 200% text the one-row header ran its actions
  // 67-230px past the screen. The row now also needs 30rem of header (37rem at 640 and
  // more everywhere wider at normal text, so nothing moves there; 18.5rem at 640 with
  // 200% text, which stacks like a phone). Below 30rem a title word wider than the line
  // may break (report 161, group 8: "Categorization Rules" at 360 with 200% text).
  return (
    <div className="@container">
      <div className="flex flex-col pb-2 sm:@min-[30rem]:flex-row sm:@min-[30rem]:items-start">
        {!hideTitle && (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="min-w-0 text-2xl font-bold text-foreground @max-[30rem]:[overflow-wrap:anywhere]">
                {title}
              </h1>
              {tooltip && (
                /* `DefinitionTooltip`, not the bare hover `Tooltip` this used to
                 be: a plain Radix tooltip opens on hover and focus and ignores
                 a click, so pressing this ⓘ did nothing at all -- eight
                 `interact.inert-control` findings across seven routes in the
                 exercise sweep (BEN-141 QF11). #803 already settled that a
                 definition needs a tap path on the web, and this primitive is
                 where that lives; it keeps hover, focus and Escape, and it
                 carries the 44px hit area and the `-m-3.5` that gives the row
                 its height back. */
                <DefinitionTooltip
                  id={`page-${pathname}`}
                  label={`About ${title}`}
                  glyphClassName="h-4 w-4"
                  className={`text-muted-foreground hover:text-foreground ${HOVER_AFFORDANCE_ONLY}`}
                >
                  {tooltip}
                </DefinitionTooltip>
              )}
            </div>
            {/* Touch has no hover, so the tooltip above can never open there.
              Same sentence, rendered inline instead — matching the standard
              page-header subtitle pattern mobile's Calendar already uses. */}
            {tooltip && (
              <p className={`text-sm text-muted-foreground ${TOUCH_INLINE_FALLBACK}`}>{tooltip}</p>
            )}
            {subtitle && (
              <p data-page-subtitle="" className="text-sm text-muted-foreground">
                {subtitle}
              </p>
            )}
          </div>
        )}
        {/* AN-52: the theme switch sits here on the dashboard only (one of three controls:
          this, Profile's Appearance, and the setup step). */}
        {/* `ml-auto` rather than `justify-between` on the parent: the title block
          is not rendered at all on the routes that own their own header, and
          `justify-between` would then put this cluster on the left. */}
        <div
          className={`ml-auto flex max-w-full flex-wrap items-center justify-end gap-2 sm:@min-[30rem]:flex-nowrap ${
            hideTitle ? "" : "mt-3 sm:@min-[30rem]:mt-0"
          }`}
        >
          <PageGuidanceToggle />
          {pathname === "/dashboard" && <ThemeToggleButton />}
          {redesign && pathname === "/home" && <ThemeToggleButton />}
          <NotificationBell dark={false} />
          <HelpButton current={pathname === "/help"} />
          {redesign ? (
            /* From lg the account menu sits at the foot of the sidebar (redesign). */
            <div className="lg:hidden">
              <HeaderAccountMenu />
            </div>
          ) : (
            <ProfileMenu />
          )}
        </div>
      </div>
    </div>
  );
}
